import React, { useState } from 'react';
import { 
  AlertCircle, CheckCircle2, Clock, Search, X, 
  Send, User, Store, ArrowRightLeft, MessageSquare, Trash2 
} from 'lucide-react';

// --- Dummy Data ---
const initialIssues = [
  { 
    id: 'TKT-001', raisedBy: 'Rahul Sharma', raisedByType: 'BUYER', target: 'Electric Supplier', 
    category: 'Payment Issue', description: 'Payment deducted but order not confirmed.', 
    dateTime: '2026-01-29 10:30 AM', status: 'PENDING',
    history: [
      { sender: 'Rahul Sharma', role: 'BUYER', message: 'I paid ₹5,000 via UPI, but it says payment failed on the site.', time: '10:30 AM' },
      { sender: 'System', role: 'SYSTEM', message: 'Ticket assigned to Senior Support.', time: '10:35 AM' }
    ]
  },
  { 
    id: 'TKT-002', raisedBy: 'Home Supplier', raisedByType: 'SELLER', target: 'Aman Varma', 
    category: 'Returns', description: 'Customer returned damaged product.', 
    dateTime: '2026-01-28 02:15 PM', status: 'PENDING',
    history: [
      { sender: 'Home Supplier', role: 'SELLER', message: 'The washing machine was returned with a broken tub.', time: '02:15 PM' }
    ]
  }
];

const IssuesPage: React.FC = () => {
  const [issues, setIssues] = useState(initialIssues);
  const [selectedIssue, setSelectedIssue] = useState<any>(null);
  const [adminReply, setAdminReply] = useState('');

  const handleSolve = (id: string) => {
    setIssues(prev => prev.map(i => i.id === id ? { ...i, status: 'SOLVED' } : i));
    if (selectedIssue?.id === id) setSelectedIssue({ ...selectedIssue, status: 'SOLVED' });
  };

  const addReply = () => {
    if (!adminReply || !selectedIssue) return;
    const newMessage = { sender: 'Super Admin', role: 'ADMIN', message: adminReply, time: 'Now' };
    setIssues(prev => prev.map(i => i.id === selectedIssue.id ? { ...i, history: [...i.history, newMessage] } : i));
    setSelectedIssue({ ...selectedIssue, history: [...selectedIssue.history, newMessage] });
    setAdminReply('');
  };

  return (
    <div className="space-y-8 pb-10">
      {/* Header */}
      <div>
        <h2 className="text-4xl font-black text-gray-900 tracking-tight">Support Desk</h2>
        <p className="text-gray-400 font-medium mt-1">Audit and resolve marketplace conflicts</p>
      </div>

      {/* Main Table (Pichla Wala Design) */}
      <div className="bg-white rounded-[40px] shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-10 py-8 border-b border-gray-50 flex justify-between items-center">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
            <input type="text" placeholder="Search tickets..." className="bg-gray-50 border-none rounded-xl py-2.5 pl-10 pr-4 text-xs font-bold w-80 outline-none" />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-50/50 text-[10px] font-black text-gray-400 uppercase tracking-widest">
              <tr>
                <th className="px-10 py-5">Ticket ID</th>
                <th className="px-6 py-5">Raised By</th>
                <th className="px-6 py-5">Against</th>
                <th className="px-6 py-5">Category</th>
                <th className="px-6 py-5">Status</th>
                <th className="px-10 py-5 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="text-sm font-bold divide-y divide-gray-50">
              {issues.map((issue) => (
                <tr key={issue.id} className="group hover:bg-gray-50/50 transition-all">
                  <td className="px-10 py-6 text-gray-400 text-xs">#{issue.id}</td>
                  <td className="px-6 py-6">
                    <div className="text-gray-900 uppercase text-xs font-black">{issue.raisedBy}</div>
                    <div className="text-[10px] text-gray-400">{issue.dateTime}</div>
                  </td>
                  <td className="px-6 py-6 text-gray-600 uppercase text-[11px]">{issue.target}</td>
                  <td className="px-6 py-6 uppercase text-[11px] tracking-tighter text-[#006d5b]">{issue.category}</td>
                  <td className="px-6 py-6">
                    <span className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase ${issue.status === 'SOLVED' ? 'bg-emerald-50 text-[#006d5b]' : 'bg-orange-50 text-orange-500 animate-pulse'}`}>
                      {issue.status}
                    </span>
                  </td>
                  <td className="px-10 py-6 text-right">
                    <button 
                      onClick={() => setSelectedIssue(issue)}
                      className="px-5 py-2 bg-gray-900 text-white text-[10px] font-black rounded-xl hover:bg-[#006d5b] transition-all"
                    >
                      VIEW DETAILS
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- CENTER POPUP MODAL --- */}
      {selectedIssue && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setSelectedIssue(null)} />
          
          <div className="relative bg-white w-full max-w-4xl rounded-[40px] shadow-2xl flex flex-col overflow-hidden max-h-[90vh]">
            {/* Modal Header */}
            <div className="p-8 border-b border-gray-100 flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#006d5b]/5 rounded-2xl flex items-center justify-center text-[#006d5b]">
                  <MessageSquare size={24} />
                </div>
                <div>
                  <h3 className="text-2xl font-black text-gray-900">{selectedIssue.id}</h3>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{selectedIssue.category}</p>
                </div>
              </div>
              <button onClick={() => setSelectedIssue(null)} className="p-3 bg-gray-50 text-gray-400 hover:text-gray-900 rounded-2xl transition-all">
                <X size={20} />
              </button>
            </div>

            {/* Modal Body (History) */}
            <div className="flex-1 overflow-y-auto p-10 space-y-8 bg-[#fafafa]">
              {/* Description Card */}
              <div className="bg-white p-6 rounded-[25px] border border-gray-100 shadow-sm">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Original Complaint</p>
                <p className="text-gray-800 font-bold leading-relaxed">{selectedIssue.description}</p>
              </div>

              {/* Chat Timeline */}
              <div className="space-y-6">
                {selectedIssue.history.map((chat: any, idx: number) => (
                  <div key={idx} className={`flex ${chat.role === 'ADMIN' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] p-5 rounded-[25px] shadow-sm ${
                      chat.role === 'ADMIN' ? 'bg-[#006d5b] text-white rounded-tr-none' : 
                      chat.role === 'SYSTEM' ? 'bg-orange-50 text-orange-700 border border-orange-100 text-center italic w-full max-w-none' :
                      'bg-white text-gray-800 border border-gray-100 rounded-tl-none'
                    }`}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-[9px] font-black uppercase tracking-tighter opacity-60">{chat.sender}</span>
                        <span className="text-[8px] opacity-40">{chat.time}</span>
                      </div>
                      <p className="text-sm font-bold">{chat.message}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer (Action Area) */}
            <div className="p-8 bg-white border-t border-gray-100">
              <textarea 
                value={adminReply}
                onChange={(e) => setAdminReply(e.target.value)}
                placeholder="Add remark or reply..."
                className="w-full bg-gray-50 border-none rounded-[25px] p-5 text-sm font-bold focus:ring-2 focus:ring-[#006d5b]/10 outline-none h-24 mb-4 resize-none"
              />
              <div className="flex justify-between items-center">
                <button className="flex items-center gap-2 text-gray-400 hover:text-gray-900 text-[10px] font-black uppercase tracking-widest transition-colors">
                  <ArrowRightLeft size={16}/> Transfer to Senior
                </button>
                <div className="flex gap-3">
                  {selectedIssue.status === 'PENDING' && (
                    <button onClick={() => handleSolve(selectedIssue.id)} className="px-8 py-3 bg-emerald-50 text-[#006d5b] text-[10px] font-black rounded-2xl uppercase hover:bg-emerald-100 transition-all">Mark Solved</button>
                  )}
                  <button onClick={addReply} className="px-10 py-3 bg-gray-900 text-white text-[10px] font-black rounded-2xl uppercase hover:bg-[#006d5b] shadow-lg transition-all">Send Remark</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IssuesPage;